import { Injectable } from '@angular/core';
import { ChapterModel } from './models/chapter.model';
import { plainToClass, classToClass } from 'class-transformer';
import { uuid } from '../shared/uuid';
import { SerializeChapterService } from 'src/app/edit/serialize/chapter/serialize-chapter.service';
import { HotspotModel } from './models/labels/hotspot.model';
import { BackgroundModel } from '../engine/models/background.model';
import { LabelsService } from './labels.service';
import { omit, isEmpty, mapKeys, times } from 'lodash';
import { LabelThemeEnum } from './models/labels/label-theme.enum';
import { HotspotThemesEnum } from './models/labels/hotspot.themes.enum';
import { ChapterUtils } from '../shared/chapter-utils';
import { defaultClippingPlanes } from './state/chapters/default-clipping-planes';
import { directionForClipId } from './models/clipping-plane.model';
import { EngineService } from '../engine/engine.service';
import { hasShownAncestor, hasShownSuccessor } from '../engine/state/nodes/build-node-state';
import { VisibilityLayersModel, VisibilityLayerModel } from './models/visibility-layers.model';
import { GraphNodeState } from 'src/lib/graph/graph.model';
import { GraphUtils } from 'src/lib/graph/graph-utils';
import { AnimationService } from '../engine/animation.service';
import { BranchTimelineModel } from '../engine/models/animation/branch-timeline.model';
import { HumanInterface } from '../engine/models/human.interface';
import { MediaModel } from './models/media.model';

declare const Human: HumanInterface;

@Injectable({
  providedIn: 'root',
})
export class ChapterService {
  constructor(
    private engineService: EngineService,
    private serializeChapterService: SerializeChapterService,
    private labelService: LabelsService,
    private animationService: AnimationService,
  ) {}

  // Update engine with added chapter
  addChapterToEngine(chapter: ChapterModel, insertIndex: number): BranchTimelineModel {
    // get chapter data from chapter model
    const chapterIdMap = ChapterUtils.makeChapterIdMap([chapter], false);
    const namespaceMap = {};
    const storyLabels = [];

    const graph = GraphUtils.createGraphFromSrc(
      Object.values(Human.scene.rootObjects),
      'objectId',
      'objects',
      new GraphNodeState(Human.scene.colorRecipes.getRecipes()),
    );

    const serialized = this.serializeChapterService.serializeChapter(
      graph,
      chapter,
      namespaceMap,
      storyLabels,
      chapterIdMap,
    );

    const chapterData = serialized;

    // copy the synchronization to the chapterData before building branch
    chapterData.synchronization = JSON.parse(JSON.stringify(chapter.synchronization));

    // create the branch from modificed data and add to timeline
    const branch = new Human.timeline.BranchTimeline(chapterData);

    Human.timeline.activeRoot.addChapter(branch, { rebuild: true, position: insertIndex });

    return branch;
  }

  // Update engine to remove chapter. This does not update application state, that
  // is handled separately in story.state.ts
  removeChapterFromEngine(chapter: ChapterModel) {
    Human.timeline.activeRoot._chapters = Human.timeline.activeRoot._chapters.filter(
      (_chapter) => _chapter.id !== chapter.id,
    );

    Human.timeline.activeRoot.build();
  }

  // make a copy of the chapter model
  copyChapter(chapter: ChapterModel, newChapterId: string): ChapterModel {
    // new object and new identifications
    const chapterCopy = classToClass(chapter);

    // generate new id
    chapterCopy.id = newChapterId;

    // generate new ids for each label
    chapterCopy.labels.forEach((label) => {
      label.id = uuid.idWithPrefix('label');
      label.chapterId = chapterCopy.id;
    });

    // copy ready state
    chapterCopy.readyState = { model: true, animation: true };

    return chapterCopy;
  }
  // return the curent chapter id
  getCurrentChapterId(): string {
    return Human.timeline.activeRoot._nowBranch.id;
  }

  // create chapterModel from human branch objects
  fromHumanChapter(
    humanModuleData: any,
    storyType: string,
    humanChapter: any,
    idx: number,
  ): ChapterModel {
    // work with a copy;
    const chapterData = { ...humanChapter };

    const humanChapterData = humanModuleData.chapters[idx];

    // copy over per chapter environment data form humanModuleData
    chapterData.environment = { ...humanChapterData.environment };

    // copy over per chapter transitions
    chapterData.transitions = { ...humanChapterData.transitions };

    // copy over transparencyMode
    if (humanChapterData.transparencyMode) {
      chapterData.transparencyMode = humanChapterData.transparencyMode;
    }

    // copy over showObjects
    chapterData.showObjects = { ...humanModuleData.chapters[idx].showObjects };
    chapterData.pickThroughObjects = { ...humanModuleData.chapters[idx].pickThroughObjects };

    // ensure every chapter has an id
    if (!chapterData.id) {
      chapterData.id = uuid.idWithPrefix('chapter');
    }

    // reformat timelinemaps
    let animated = false;

    chapterData.timelineMap = Object.values(humanChapter.maps).map((timelineMap: any) => {
      if (timelineMap.leafTimeline.id !== 'dummy') {
        animated = true;
      }

      return this.animationService.fromEngineTimelineMap(timelineMap);
    });

    delete chapterData.maps;

    // reformat animation -> ui
    let playCount = 0; // default to scrub
    const looping = humanChapter.animation ? humanChapter.animation.loop : false;
    if (animated && looping) {
      playCount = -1;
    } else if (animated && !looping) {
      playCount = 1;
    }
    chapterData.ui =
      humanChapter.ui && !isEmpty(humanChapter.ui)
        ? humanChapter.ui
        : { play: { count: playCount } };

    // convert annotations to labels

    const theme = LabelThemeEnum.Annotation;

    const annotationLabels = [...(chapterData.annotations || [])].map((annotation) => {
      const label = this.labelService.deserializeAnnotation(annotation, chapterData, theme);
      return label;
    });

    // filter and convert hotspots to labels
    const legacyHotspots = [];
    const hotspotGroups = this.groupHotspots(
      [...(chapterData.hotspots || [])].filter((hotspot) => {
        const convertable = this.labelService.filterHotspot(humanModuleData, humanChapter, hotspot);
        if (!convertable) {
          legacyHotspots.push(hotspot);
        } else {
          if (!hotspot.theme) {
            // set default theme by story type
            hotspot.theme =
              storyType === 'module'
                ? HotspotThemesEnum.studioNext
                : HotspotThemesEnum.classicHotspot;
          }
        }
        return convertable;
      }),
    );

    const hotspotLabels = hotspotGroups.map((hotspotGroup) =>
      this.labelService.deserializeHotspots(humanModuleData, humanChapter, idx, ...hotspotGroup),
    );
    chapterData.labels = [...annotationLabels, ...hotspotLabels];
    delete chapterData.annotations;

    // reformat chapter background
    chapterData.background = BackgroundModel.deserialize(humanChapter.background);

    // return instance of ChapterModel
    const chapter = plainToClass(ChapterModel, chapterData as Object);
    chapter.hotspots = legacyHotspots;
    chapter.readyState = { model: false, animation: false };

    // set default clipping plane data if none
    // boundaries are set later, because they haven't been calculated by the engine yet.
    chapter.clippingPlanes = isEmpty(chapter.clippingPlanes)
      ? defaultClippingPlanes
      : this.sanitizeClippingPlanes(chapter.clippingPlanes);

    // legend
    const legend = humanChapterData.legend || [];
    chapter.legend = [...legend];

    // visibilityLayers
    chapter.visibilityLayers = this.buildVisibilityLayers(
      humanChapterData.visibilityLayers,
      chapter.showObjects,
    );

    chapter.colorRecipeObjects = humanChapterData.colorRecipeObjects;

    // Content Blocks
    const contentBlocks = humanChapterData.contentBlocks || [];
    chapter.contentBlocks = [...contentBlocks];

    // Quiz Questions: de-serialize w/ chapter id
    const quizQuestions = humanChapterData.quizQuestions || [];
    chapter.quizQuestions = quizQuestions.map((quizQuestion) => {
      const deserializedQuestion = {
        ...quizQuestion,
        chapterId: chapterData.id,
      };
      return deserializedQuestion;
    });

    // Initialize default media language.
    const media = humanChapter.media || {};

    Object.values(media).forEach((mediaObject: MediaModel) => {
      if (!mediaObject.data.hasOwnProperty('lang')) {
        mediaObject.data.lang = 'en';
      }
    });

    chapter.media = media;

    return chapter;
  }

  // Chapter timelineMaps do not define the full library path, only the top level state
  getChapterTweenStates(chapter: ChapterModel): string[] {
    const tweenStates = chapter.timelineMap
      .filter((timelineMap) => {
        // Exclude 'dummy', it is not a state
        return timelineMap.data.timeline !== 'dummy';
      })
      .map((timelineMap) => timelineMap.data.timeline);

    return Array.from(new Set(tweenStates));
  }

  getChapterLightLibraries(chapter: ChapterModel): string[] {
    const lightLibraries = (chapter.lights && chapter.lights.libraries) || [];

    // 'default' is not a loadable light library
    return lightLibraries.filter((lightLibrary) => lightLibrary !== 'default');
  }

  // PBR Reflection assets live in the chapter's environment object
  getChapterReflectionLibraries(chapter: ChapterModel): string[] {
    const lightMaps = (chapter.environment && chapter.environment.lightMap) || [];
    const reflectionMaps = (chapter.environment && chapter.environment.reflectionMap) || [];

    const extractLibId = (reflectionAsset: string) => {
      return reflectionAsset.split('.')[0];
    };

    const libIds = lightMaps.map(extractLibId).concat(reflectionMaps.map(extractLibId));

    return Array.from(new Set(libIds));
  }

  hasVisibilityLayers(visibilityLayers: VisibilityLayerModel[]): boolean {
    return Array.isArray(visibilityLayers) && visibilityLayers.length > 0;
  }

  private buildVisibilityLayers(
    _visibilityLayers: any,
    showObjects: object,
  ): VisibilityLayersModel {
    const visibilityLayers = { src: {}, current: {} };

    if (_visibilityLayers && this.hasVisibilityLayers(_visibilityLayers.layers)) {
      const initIndex = _visibilityLayers.initIndex || 0;

      const unpackedLayers = this.unpackLayers(_visibilityLayers.layers);
      const shownObjectsSet = this.buildShownObjectsSet(showObjects);

      const currentLayers = this.buildCurrentLayers(unpackedLayers, shownObjectsSet);

      visibilityLayers.src = {
        layers: _visibilityLayers.layers,
        initIndex: initIndex,
      };

      visibilityLayers.current = {
        layers: currentLayers,
        index: initIndex,
        position: 0,
      };
    }

    return visibilityLayers;
  }

  private buildShownObjectsSet(showObjects: object): Set<string> {
    const graph = this.engineService.getSceneGraph(Human.scene.rootObjects);

    const shownObjects = Object.keys(graph.nodes).filter((objectId) => {
      return (
        hasShownAncestor(graph, objectId, showObjects) ||
        hasShownSuccessor(graph, objectId, showObjects)
      );
    });

    return new Set(shownObjects);
  }

  // Unpack layer objects to their respective leaves, return new deep copy
  private unpackLayers(layers: VisibilityLayerModel[]): VisibilityLayerModel[] {
    const srcLayers = [];

    layers.forEach((layer) => {
      let objectIds = [];

      layer.objectIds.forEach((objectId) => {
        const leaves = Human.scene.objects[objectId].getLeafObjects();
        objectIds = objectIds.concat(leaves.map((leaf) => leaf.objectId));
      });

      const newLayer = { name: layer.name, objectIds: objectIds };
      srcLayers.push(newLayer);
    });

    return srcLayers;
  }

  private buildCurrentLayers(
    srcLayers: VisibilityLayerModel[],
    shownObjectsSet: Set<string>,
  ): VisibilityLayerModel[] {
    const currentLayers = [];

    srcLayers.forEach((layer, layerIndex) => {
      let layerBuilt = false;

      layer.objectIds.forEach((objectId) => {
        if (shownObjectsSet.has(objectId)) {
          if (layerBuilt) {
            currentLayers[currentLayers.length - 1].objectIds.push(objectId);
          } else {
            const newLayer = {
              name: srcLayers[layerIndex].name,
              objectIds: [objectId],
            };

            currentLayers.push(newLayer);
            layerBuilt = true;
          }
        }
      });
    });

    return currentLayers;
  }

  private sanitizeClippingPlanes(clippingPlanes: { clips: { [x: string]: any }; boundary?: any }) {
    const enabled = Object.values(clippingPlanes.clips).some((c) => c.progress > 0);
    const clips = mapKeys(clippingPlanes.clips, (value, key) => {
      return directionForClipId[key] || key;
    });

    return {
      ...clippingPlanes,
      clips,
      enabled,
    } as ChapterModel['clippingPlanes'];
  }

  // two hotspots can be combined into one label
  private groupHotspots(hotspots: HotspotModel[]): Array<Array<HotspotModel>> {
    const hotspotGroups: { [key: string]: any[] } = {};

    // helper functions to key hotspots that are reciprocal
    const keyPosition = (hotspot: HotspotModel): string => {
      if (hotspot.primitiveIndex) {
        return hotspot.primitiveIndex.toFixed(0);
      } else if (hotspot.pos) {
        return hotspot.pos.map((c) => c.toFixed(3)).join(':');
      } else {
        return Math.random().toFixed(3);
      }
    };

    const keyType = (type: string): string => {
      return RegExp('^circle-(plus|minus)').test(type) ? 'pair' : 'single';
    };

    // group hotspots by key
    hotspots.forEach((hotspot) => {
      const groupKey = [keyType(hotspot.type), hotspot.objectId, keyPosition(hotspot)].join('|');
      if (groupKey in hotspotGroups) {
        hotspotGroups[groupKey].push(hotspot);
      } else {
        hotspotGroups[groupKey] = [hotspot];
      }
    });

    return Object.values(hotspotGroups);
  }
}
